import type { Locale } from '@seek/melways-sites';
import {
  Box,
  ButtonIcon,
  Column,
  Columns,
  ContentBlock,
  IconClear,
} from 'braid-design-system';
import { useEffect } from 'react';

import { analyticsEvents } from 'src/constants/analytics';
import { trackPageInteraction } from 'src/modules/AnalyticsTracker';

import FindRecruiterContent from './FindRecruiterContent';
import FindRecruiterImage from './FindRecruiterImage';
import { useFindRecruiterDialogTracking } from './useFindRecruiterDialogTracking';

import * as styles from './FindRecruiterDialog.css';

interface Props {
  onClose: () => void;
  locale: Locale;
}

const FindRecruiterDialog = ({ onClose, locale }: Props) => {
  const trackingData = useFindRecruiterDialogTracking();

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Prevent scrolling in the background when the dialog is open (optional)
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Close the dialog upon pressing Esc key
  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', close);

    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  // Tracking ✨
  useEffect(() => {
    trackPageInteraction(
      analyticsEvents.TRACK_VIEW_DIALOG,
      undefined,
      trackingData,
    );
  }, [trackingData]);

  return (
    <Box className={styles.dialogBackdrop} onClick={handleBackdropClick}>
      <Box background="body" className={styles.dialogContent}>
        {/* Close button */}
        <Box className={styles.dialogClose}>
          <ButtonIcon
            id="close-btn"
            icon={<IconClear />}
            label="Close"
            onClick={onClose}
            size="small"
          />
        </Box>

        <ContentBlock width="small" align="center">
          <Columns space="none" reverse collapseBelow="desktop">
            <Column>
              <FindRecruiterImage locale={locale} />
            </Column>
            <Column>
              <FindRecruiterContent locale={locale} onClose={onClose} />
            </Column>
          </Columns>
        </ContentBlock>
      </Box>
    </Box>
  );
};

export default FindRecruiterDialog;
